import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { XIcon, Loader2Icon, PhoneIcon, ShieldCheckIcon, Trash2Icon } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
// Validation Schema - Only phone is required
const editGuardianContactSchema = z.object({
  guardianName: z.string().optional(),
  guardianPhone: z.string().min(1, 'Phone number is required').regex(/^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/, 'Please enter a valid phone number')
});
type EditGuardianContactFormValues = z.infer<typeof editGuardianContactSchema>;
interface EditGuardianContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EditGuardianContactFormValues) => Promise<void>;
  onRemove?: () => Promise<void>;
  studentName: string;
  currentContact?: {
    name: string;
    phone: string;
  };
}
export const EditGuardianContactModal: React.FC<EditGuardianContactModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onRemove,
  studentName,
  currentContact
}) => {
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    },
    reset
  } = useForm<EditGuardianContactFormValues>({
    resolver: zodResolver(editGuardianContactSchema),
    defaultValues: {
      guardianName: currentContact?.name || '',
      guardianPhone: currentContact?.phone || ''
    }
  });
  const onSubmit = async (data: EditGuardianContactFormValues) => {
    try {
      await onSave(data);
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to update guardian contact:', error);
    }
  };
  const handleRemoveClick = () => {
    setShowRemoveConfirm(true);
  };
  const handleRemoveConfirm = async () => {
    if (!onRemove) return;
    setIsRemoving(true);
    try {
      await onRemove();
      reset();
      setShowRemoveConfirm(false);
      onClose();
    } catch (error) {
      console.error('Failed to remove guardian contact:', error);
    } finally {
      setIsRemoving(false);
    }
  };
  // Reset form when modal opens with current values
  useEffect(() => {
    if (isOpen) {
      reset({
        guardianName: currentContact?.name || '',
        guardianPhone: currentContact?.phone || ''
      });
      setShowRemoveConfirm(false);
    }
  }, [isOpen, currentContact, reset]);
  return <Dialog.Root open={isOpen} onOpenChange={onClose} data-id="element-1362">
      <AnimatePresence data-id="element-1363">
        {isOpen && <Dialog.Portal forceMount container={document.body} data-id="element-1364">
            <Dialog.Overlay asChild data-id="element-1365">
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{
            zIndex: 9998
          }} data-id="element-1366">
                <Dialog.Content asChild data-id="element-1367">
                  <motion.div initial={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} className="w-full max-w-md max-h-[85vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden relative" style={{
                zIndex: 9999
              }} onClick={e => e.stopPropagation()} data-id="element-1368">
                    {/* Header - Fixed */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50 flex-shrink-0" data-id="element-1369">
                      <div className="flex items-center gap-3" data-id="element-1370">
                        <div className="w-10 h-10 rounded-xl bg-[#00B75F]/10 flex items-center justify-center text-[#00B75F]" data-id="element-1371">
                          <PhoneIcon size={20} data-id="element-1372" />
                        </div>
                        <div data-id="element-1373">
                          <Dialog.Title className="text-lg font-bold text-text-primary" data-id="element-1374">
                            {currentContact ? 'Edit' : 'Add'} Report Contact
                          </Dialog.Title>
                          <Dialog.Description className="text-xs text-text-secondary" data-id="element-1375">
                            For {studentName}
                          </Dialog.Description>
                        </div>
                      </div>
                      <button onClick={onClose} className="p-2 text-text-tertiary hover:text-text-primary hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1376">
                        <XIcon size={20} data-id="element-1377" />
                      </button>
                    </div>

                    {/* Form - Scrollable */}
                    <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto" data-id="element-1378">
                      <div className="p-6 space-y-5" data-id="element-1379">
                        {/* Privacy Notice */}
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3" data-id="element-1380">
                          <div className="flex gap-2" data-id="element-1381">
                            <ShieldCheckIcon size={14} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1382" />
                            <div className="text-xs text-blue-900 leading-relaxed" data-id="element-1383">
                              <p className="font-semibold mb-1" data-id="element-1384">
                                Privacy Protected
                              </p>
                              <p data-id="element-1385">
                                This information is private and used only for
                                sharing student reports. It will not be used for
                                any other purpose.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Guardian Name Field - Optional */}
                        <div className="space-y-1.5" data-id="element-1386">
                          <label className="text-sm font-semibold text-text-primary" data-id="element-1387">
                            Guardian Name{' '}
                            <span className="text-text-tertiary font-normal text-xs" data-id="element-1388">
                              (Optional)
                            </span>
                          </label>
                          <input {...register('guardianName')} placeholder="e.g. Mary Johnson" className="w-full px-3 py-2.5 bg-white border border-custom rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 focus:border-[#00B75F] transition-all" data-id="element-1389" />
                        </div>

                        {/* Phone Number Field - Required */}
                        <div className="space-y-1.5" data-id="element-1390">
                          <label className="text-sm font-semibold text-text-primary" data-id="element-1391">
                            Phone Number <span className="text-red-500" data-id="element-1392">*</span>
                          </label>
                          <input {...register('guardianPhone')} type="tel" placeholder="e.g. +1 (555) 123-4567" className={`w-full px-3 py-2.5 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 transition-all ${errors.guardianPhone ? 'border-red-300 focus:border-red-500' : 'border-custom focus:border-[#00B75F]'}`} data-id="element-1393" />
                          {errors.guardianPhone && <p className="text-xs text-red-500 font-medium" data-id="element-1394">
                              {errors.guardianPhone.message}
                            </p>}
                          <p className="text-xs text-text-tertiary" data-id="element-1395">
                            Include country code for international numbers
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="pt-4 space-y-3" data-id="element-1396">
                          <div className="flex items-center gap-3" data-id="element-1397">
                            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-custom rounded-xl text-sm font-semibold text-text-secondary hover:bg-gray-50 transition-colors" data-id="element-1398">
                              Cancel
                            </button>
                            <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2.5 bg-[#00B75F] text-white rounded-xl text-sm font-semibold hover:bg-[#00a055] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1399">
                              {isSubmitting ? <>
                                  <Loader2Icon size={16} className="animate-spin" data-id="element-1400" />
                                  Saving...
                                </> : 'Save Contact'}
                            </button>
                          </div>

                          {/* Remove Contact Button - Only show if contact exists */}
                          {currentContact && onRemove && <button type="button" onClick={handleRemoveClick} disabled={isRemoving || isSubmitting} className="w-full px-4 py-2.5 bg-red-50 text-red-600 rounded-xl text-sm font-semibold hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-red-200" data-id="element-1401">
                              <Trash2Icon size={16} data-id="element-1402" />
                              Remove Contact
                            </button>}
                        </div>
                      </div>
                    </form>
                  </motion.div>
                </Dialog.Content>
              </motion.div>
            </Dialog.Overlay>
          </Dialog.Portal>}
      </AnimatePresence>

      {/* Remove Confirmation Dialog */}
      <AnimatePresence data-id="element-1403">
        {showRemoveConfirm && <Dialog.Portal forceMount container={document.body} data-id="element-1404">
            <Dialog.Overlay asChild data-id="element-1405">
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{
            zIndex: 10000
          }} data-id="element-1406">
                <Dialog.Content asChild data-id="element-1407">
                  <motion.div initial={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} className="bg-surface-elevated rounded-2xl shadow-2xl max-w-md w-full border border-custom" onClick={e => e.stopPropagation()} data-id="element-1408">
                    {/* Header */}
                    <div className="flex items-start justify-between p-6 border-b border-custom" data-id="element-1409">
                      <div className="flex items-start gap-4" data-id="element-1410">
                        <div className="w-12 h-12 bg-red-500 bg-opacity-10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1411">
                          <Trash2Icon size={24} className="text-red-600" data-id="element-1412" />
                        </div>
                        <div className="flex-1" data-id="element-1413">
                          <h2 className="text-lg font-bold text-text-primary mb-1" data-id="element-1414">
                            Remove Guardian Contact?
                          </h2>
                          <p className="text-sm text-text-secondary" data-id="element-1415">
                            This will permanently remove the guardian contact
                            information for {studentName}.
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setShowRemoveConfirm(false)} disabled={isRemoving} className="p-2 rounded-lg hover:bg-surface transition-colors flex-shrink-0" aria-label="Close" data-id="element-1416">
                        <XIcon size={20} className="text-text-secondary" data-id="element-1417" />
                      </button>
                    </div>

                    {/* Contact Info */}
                    <div className="px-6 pt-4" data-id="element-1418">
                      <div className="bg-surface rounded-lg p-3 border border-custom" data-id="element-1419">
                        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2" data-id="element-1420">
                          Contact to Remove
                        </p>
                        {currentContact?.name && <p className="text-sm font-semibold text-text-primary mb-1" data-id="element-1421">
                            {currentContact.name}
                          </p>}
                        <p className="text-sm text-text-secondary" data-id="element-1422">
                          {currentContact?.phone}
                        </p>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 p-6" data-id="element-1423">
                      <button onClick={() => setShowRemoveConfirm(false)} disabled={isRemoving} className="px-6 py-2.5 border-2 border-custom text-text-primary rounded-lg font-semibold hover:bg-surface transition-colors disabled:opacity-50" data-id="element-1424">
                        Cancel
                      </button>
                      <button onClick={handleRemoveConfirm} disabled={isRemoving} className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-1425">
                        {isRemoving ? <>
                            <Loader2Icon size={16} className="animate-spin" data-id="element-1426" />
                            Removing...
                          </> : 'Remove Contact'}
                      </button>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </motion.div>
            </Dialog.Overlay>
          </Dialog.Portal>}
      </AnimatePresence>
    </Dialog.Root>;
};